import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Trash2, ArrowRight, MapPin, ChevronRight, CheckCircle2, Lock } from "lucide-react";
import { useCompareBucket } from "../context/CompareBucketContext";
import { useOwnedGrids } from "../context/OwnedGridsContext";
import { idLabel } from "../lib/idLabels";
import { getBillingStatus, getOwnedCities, getCityPrice, openCity, getDeepAnalysisQuote } from "../lib/api";


// ─── Credit confirmation ───────────────────────────────────────────────────────
// Pro pays deep analysis from its credit balance, so there is no payment method to
// pick — only the price to confirm before the billed run starts. Same quote the
// Deep Analysis page uses for a radius change; 3 km is the radius the report opens at.

function CreditConfirmModal({ cell, onConfirm, onCancel }) {
  const [quote, setQuote]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getDeepAnalysisQuote(cell.gridId, cell.subcategory, "3km")
      .then((q) => { if (alive) setQuote(q); })
      .catch(() => { if (alive) setQuote(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [cell.gridId, cell.subcategory]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 pt-6 pb-4">
          <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center mb-3">
            <BarChart3 className="w-5 h-5 text-brand-600" />
          </div>
          <h2 className="text-base font-bold text-slate-800 mb-1">Jalankan analisis mendalam?</h2>
          <p className="text-xs text-slate-500 leading-relaxed">
            Grid <span className="font-mono font-semibold text-slate-700">#{cell.gridId}</span> belum punya
            laporan untuk bisnis ini. Biayanya dipotong dari saldo kredit Anda.
          </p>
        </div>
        <div className="mx-6 mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          {loading ? (
            <p className="text-xs text-slate-400">Menghitung biaya…</p>
          ) : quote?.billing_enabled === false ? (
            <p className="text-[11px] text-slate-500">Penagihan nonaktif — laporan ini gratis di lingkungan ini.</p>
          ) : quote ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Biaya laporan (radius 3 km)</span>
              <span className="text-sm font-extrabold text-slate-800 tabular-nums">
                Rp {Math.round(quote.price || 0).toLocaleString("id-ID")}
              </span>
            </div>
          ) : (
            <p className="text-[11px] text-amber-700">Tidak bisa memuat biaya. Anda akan ditagih sesuai tarif saat menjalankan.</p>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-2">
          <button onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
            Batal
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-brand-700 hover:bg-brand-800 transition-colors disabled:opacity-50">
            Jalankan
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Score helpers ─────────────────────────────────────────────────────────────

// Same 6-tier palette + labels as the Analysis map (Low…Best).
const SCORE_COLORS = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#4ade80", "#15803d"];
const SUITABILITY  = ["Low", "Fair", "Moderate", "Good", "High", "Best"];
const SCORE_CUTS   = [20, 40, 60, 80, 90];   // absolute fallback

function scoreBucket(s) {
  const v = s ?? 0;
  let i = 0;
  while (i < SCORE_CUTS.length && v >= SCORE_CUTS[i]) i++;
  return i;
}
function scoreColor(s) { return SCORE_COLORS[scoreBucket(s)]; }
// A cell added from the map carries its per-city tier — use it so the compare card
// shows the same colour/label the cell had on the map; fall back to absolute.
function cellTierIdx(cell) {
  if (cell && Number.isInteger(cell.suitabilityIdx)) return cell.suitabilityIdx;
  if (cell && cell.suitability) { const i = SUITABILITY.indexOf(cell.suitability); if (i >= 0) return i; }
  return scoreBucket(cell?.score ?? 0);
}

// ─── Zone card ─────────────────────────────────────────────────────────────────

function ZoneCard({ cell, onRemove, onDeepAnalysis, locked, cityPrice, onOpenCity, unlocking }) {
  const score   = cell.score ?? 0;
  const idx     = cellTierIdx(cell);
  const color   = SCORE_COLORS[idx];
  const label   = idLabel(SUITABILITY[idx]);
  const { ownedIds } = useOwnedGrids();
  const isOwned = ownedIds.includes(cell.gridId);
  const paid    = isOwned;

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col transition-colors ${
      isOwned ? "border-slate-300 shadow-amber-100" : "border-slate-200"
    }`}>
      {/* Color bar */}
      <div className="h-1.5 w-full" style={{ backgroundColor: locked ? "#e2e8f0" : color }} />

      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate leading-snug">
              {cell.namaKelurahan || cell.kode_kelurahan || "—"}
            </p>
            {cell.namaKecamatan && (
              <p className="text-[11px] text-slate-500 truncate mt-0.5">Kec. {cell.namaKecamatan}</p>
            )}
            {cell.namaKabkot && (
              <div className="flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                <p className="text-[10px] text-slate-400 truncate">{cell.namaKabkot}</p>
              </div>
            )}
            <div className="flex items-center gap-1.5 mt-1.5">
              <p className="text-[10px] font-mono text-slate-300">Grid #{cell.gridId}</p>
              {isOwned && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">
                  <CheckCircle2 className="w-2.5 h-2.5" /> DIMILIKI
                </span>
              )}
            </div>
          </div>
          <button onClick={() => onRemove(cell.gridId)} className="flex items-center gap-1 text-red-300 hover:text-red-500 transition-colors shrink-0 mt-0.5">
            <Trash2 className="w-4 h-4" />
            <span className="text-xs font-medium">Hapus</span>
          </button>
        </div>

        {locked ? (
          /* Scores hidden until the city is open */
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-3 flex items-start gap-2.5">
            <Lock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs leading-snug flex-1 min-w-0">
              <span className="font-bold text-slate-800">Skor disembunyikan</span>
              <span className="text-slate-500"> — buka {cell.namaKabkot || "kota ini"} untuk menampilkan Skor Lokasi, Populasi &amp; Akses Jalan. Analisis mendalam tetap berfungsi.</span>
              <button
                onClick={() => onOpenCity(cell.kode_kabkot)}
                disabled={unlocking}
                className="mt-2 block w-full rounded-lg bg-brand-700 px-3 py-2 text-xs font-bold text-white hover:bg-brand-800 transition-colors disabled:opacity-50"
              >
                {unlocking ? "…" : cityPrice ? `Buka kota · Rp ${Number(cityPrice.price).toLocaleString("id-ID")}/thn` : "Buka kota"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Score */}
            <div className="flex items-end gap-3">
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Skor Lokasi</p>
                <p className="text-3xl font-extrabold leading-none mt-0.5" style={{ color }}>{score}</p>
                <p className="text-xs font-semibold mt-0.5" style={{ color }}>{label}</p>
              </div>
              <div className="ml-auto text-right space-y-1.5">
                <div>
                  <p className="text-[10px] text-slate-400">Populasi</p>
                  <p className="text-base font-bold leading-none" style={{ color: scoreColor(cell.pop_score ?? 0) }}>
                    {cell.pop_score ?? "—"}
                    <span className="text-[10px] font-normal text-slate-300">/100</span>
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Akses Jalan</p>
                  <p className="text-base font-bold leading-none" style={{ color: scoreColor(cell.road_score ?? 0) }}>
                    {cell.road_score ?? "—"}
                    <span className="text-[10px] font-normal text-slate-300">/100</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Score bar */}
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, backgroundColor: color }} />
            </div>
          </>
        )}

        {/* Meta */}
        <div className="flex gap-1.5 flex-wrap">
          {cell.subcategory && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-50 text-brand-700 font-semibold">{cell.subcategory}</span>
          )}
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold capitalize">
            {(cell.target_ses || "all").replace(/_/g, " ")}
          </span>
        </div>

        {/* Deep Analysis — what you get */}
        <div className="mt-auto">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Analisis Mendalam memberi Anda</p>
          <ul className="space-y-1 mb-2.5">
            {[
              "Skor lokasi untuk titik ini secara spesifik",
              "Kompetitor terdekat, anchor & lalu lintas pejalan kaki",
              "Narasi AI + panduan mitigasi risiko, ekspor ke PDF",
            ].map((t) => (
              <li key={t} className="flex items-start gap-1.5 text-[11px] text-slate-500 leading-snug">
                <CheckCircle2 className="w-3 h-3 text-brand-600 shrink-0 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={() => onDeepAnalysis(cell)}
            className="flex items-center justify-center gap-1.5 w-full px-3 py-2.5 rounded-xl text-xs font-semibold transition-all"
            style={paid
              ? { backgroundColor: "#14543e", color: "#fff" }
              : { backgroundColor: "#f8fafc", color: "#334155", border: "1px solid #e2e8f0" }
            }
          >
            <BarChart3 className="w-3.5 h-3.5" />
            {paid ? "Buka Analisis Mendalam" : "Jalankan Analisis Mendalam · Rp 100rb"}
            <ChevronRight className="w-3.5 h-3.5 ml-auto" />
          </button>
          {locked && (
            <p className="text-[10px] text-slate-400 text-center mt-1.5 leading-snug">
              Tetap berfungsi tanpa membuka kota — Anda hanya membayar untuk laporan ini.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function Comparison() {
  const navigate = useNavigate();
  const { bucket, remove, maxZones } = useCompareBucket();
  const { saveZone, ownedIds } = useOwnedGrids();
  const [confirmCell, setConfirmCell] = useState(null);

  // ── City-score gate: scores belong to the whole-city grid purchase. A zone
  //    whose city isn't open shows no score numbers (deep analysis still works).
  //    Bucket can hold zones from several cities, so gate per kode_kabkot.
  const [billingOn,   setBillingOn]   = useState(false);
  const [openCities,  setOpenCities]  = useState(() => new Set());
  const [cityPrices,  setCityPrices]  = useState({});   // kode_kabkot → price obj
  const [unlockingCity, setUnlockingCity] = useState(null);

  const profileId = (() => {
    try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}").id || null; }
    catch { return null; }
  })();

  const refreshCityLocks = useCallback(async () => {
    try {
      const s = await getBillingStatus();
      setBillingOn(s.enabled);
      if (!s.enabled || !profileId) { setOpenCities(new Set()); return; }
      const cities = await getOwnedCities(profileId);
      setOpenCities(new Set(cities.filter((c) => c.is_active).map((c) => c.kode_kabkot)));
    } catch { setBillingOn(false); }
  }, [profileId]);
  useEffect(() => { refreshCityLocks(); }, [refreshCityLocks]);

  const cityLocked = useCallback(
    (cell) => billingOn && !!cell.kode_kabkot && !openCities.has(cell.kode_kabkot),
    [billingOn, openCities],
  );

  // Lazily fetch prices for the locked cities in the bucket.
  useEffect(() => {
    const need = [...new Set(bucket.filter(cityLocked).map((c) => c.kode_kabkot))]
      .filter((k) => k && !(k in cityPrices));
    if (!need.length) return;
    let alive = true;
    Promise.all(need.map((k) => getCityPrice(k).then((p) => [k, p]).catch(() => [k, null])))
      .then((pairs) => { if (alive) setCityPrices((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { alive = false; };
  }, [bucket, cityLocked, cityPrices]);

  async function handleOpenCity(kodeKabkot) {
    if (!profileId || !kodeKabkot) return;
    setUnlockingCity(kodeKabkot);
    try { await openCity(profileId, kodeKabkot); await refreshCityLocks(); }
    catch (e) { alert(e.message); } finally { setUnlockingCity(null); }
  }

  function handleDeepAnalysis(cell) {
    const paid = ownedIds.includes(cell.gridId);
    if (paid) {
      launchDeepAnalysis(cell);
    } else {
      setConfirmCell(cell);
    }
  }

  function launchDeepAnalysis(cell) {
    let profileId = null;
    try { profileId = JSON.parse(localStorage.getItem("landhep_profile") || "{}").id || null; } catch { /* ignore */ }
    saveZone({
      gridId:         cell.gridId,
      profileId,
      kode_kelurahan: cell.kode_kelurahan || "",
      kode_kabkot:    cell.kode_kabkot    || "",
      siteScore:      cell.score,
      areaFit:        cell.area_fit,
      demandMult:     cell.demand_mult,
      // Full context so the zone survives a page refresh (loaded from DB alone).
      lat:            cell.lat,
      lng:            cell.lng,
      subcategory:    cell.subcategory,
      target_ses:     cell.target_ses,
      namaKelurahan:  cell.namaKelurahan,
      namaKecamatan:  cell.namaKecamatan,
      namaKabkot:     cell.namaKabkot,
      namaProvinsi:   cell.namaProvinsi,
    });
    // One-shot hand-over: the report page may run the first (paid) analysis of
    // this grid only because the user just confirmed it here. Consumed on read.
    try { sessionStorage.setItem("landhep_da_intent", String(cell.gridId)); } catch { /* ignore */ }
    navigate("/deep-analysis", { state: { cell } });
  }

  if (bucket.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-lg font-bold text-slate-700 mb-2">Belum ada zona dipilih</h2>
          <p className="text-sm text-slate-400 mb-6">Buka Analisis dan tambahkan sel grid ke keranjang perbandingan Anda.</p>
          <button
            onClick={() => navigate("/analysis")}
            className="flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 transition-colors mx-auto"
          >
            Buka Analisis <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Page header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-800">Perbandingan Zona</h1>
            <p className="text-sm text-slate-400 mt-1">{bucket.length} zona{bucket.length > 1 ? "" : ""} dipilih · maks {maxZones}</p>
          </div>
          <button
            onClick={() => navigate("/analysis")}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 transition-colors"
          >
            <ArrowRight className="w-4 h-4 rotate-180" /> Kembali ke Peta
          </button>
        </div>

        {/* Zone cards grid */}
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(bucket.length, 3)}, 1fr)` }}>
          {bucket.map((cell) => (
            <ZoneCard
              key={cell.gridId}
              cell={cell}
              onRemove={remove}
              onDeepAnalysis={handleDeepAnalysis}
              locked={cityLocked(cell)}
              cityPrice={cityPrices[cell.kode_kabkot]}
              onOpenCity={handleOpenCity}
              unlocking={unlockingCity === cell.kode_kabkot}
            />
          ))}
        </div>

        {/* Score comparison bar chart */}
        {bucket.length > 1 && (
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-sm font-bold text-slate-700 mb-4">Perbandingan Skor</h2>
            <div className="flex flex-col gap-3">
              {bucket.map((cell) => {
                const idx    = cellTierIdx(cell);
                const color  = SCORE_COLORS[idx];
                const locked = cityLocked(cell);
                return (
                  <div key={cell.gridId} className="flex items-center gap-3">
                    <span className="text-[10px] text-slate-500 font-semibold w-28 shrink-0 truncate">
                      {cell.namaKelurahan || `#${cell.gridId}`}
                    </span>
                    {locked ? (
                      <>
                        <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden flex items-center px-3">
                          <div className="h-1.5 flex-1 rounded-full bg-[repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0_6px,#f1f5f9_6px,#f1f5f9_12px)]" />
                        </div>
                        <span className="flex items-center gap-1 text-[10px] font-semibold w-16 shrink-0 text-slate-400">
                          <Lock className="w-3 h-3" /> Tersembunyi
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-700"
                            style={{ width: `${cell.score}%`, backgroundColor: color }}
                          >
                            <span className="text-[10px] font-bold text-white">{cell.score}</span>
                          </div>
                        </div>
                        <span className="text-xs font-semibold w-16 shrink-0" style={{ color }}>{idLabel(SUITABILITY[idx])}</span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Credit confirmation */}
      {confirmCell && (
        <CreditConfirmModal
          cell={confirmCell}
          onConfirm={() => { setConfirmCell(null); launchDeepAnalysis(confirmCell); }}
          onCancel={() => setConfirmCell(null)}
        />
      )}
    </div>
  );
}
